import { normalizeAnnotation, type EntryAnnotation, type EntryAnnotations } from './annotations';

/** Root meta file for repo annotations (and future meta). Hidden in the UI like `.git`. */
export const TRV_META_PATH = '.trv';

/** Placeholder that materializes an empty folder in the virtual tree. Hidden in listings. */
export const KEEP_FILE_NAME = '.keep';

export const TRV_MIME_TYPE = 'application/x-trove-meta+json';

export const TRV_META_VERSION = 1 as const;

export interface TrvMetaFile {
  path: string;
  content?: string;
  mimeType: string;
  size: number;
  modifiedAt: string;
}

export interface TrvMeta {
  version: typeof TRV_META_VERSION;
  annotations?: EntryAnnotations;
}

/** Paths the product treats as internal meta — never shown in file tree / lists. */
export const isHiddenMetaPath = (path: string): boolean => {
  const normalized = path.replace(/^\/+|\/+$/g, '');
  return normalized === TRV_META_PATH || normalized.startsWith(`${TRV_META_PATH}/`);
};

/** Empty-folder placeholders: kept in the snapshot, omitted from visible listings. */
export const isKeepPath = (path: string): boolean => {
  const normalized = path.replace(/^\/+|\/+$/g, '');
  return normalized === KEEP_FILE_NAME || normalized.endsWith(`/${KEEP_FILE_NAME}`);
};

export const keepPathForFolder = (folder: string): string =>
  folder ? `${folder.replace(/\/+$/, '')}/${KEEP_FILE_NAME}` : KEEP_FILE_NAME;

export const visibleFiles = <T extends { path: string }>(files: T[]): T[] =>
  files.filter((file) => !isHiddenMetaPath(file.path) && !isKeepPath(file.path));

const encoder = new TextEncoder();

export const parseTrvMeta = (content: string): TrvMeta => {
  try {
    const raw = JSON.parse(content) as {
      version?: unknown;
      annotations?: Record<string, EntryAnnotation | null | undefined>;
    };
    const annotations: EntryAnnotations = {};
    for (const [path, value] of Object.entries(raw.annotations ?? {})) {
      if (isHiddenMetaPath(path)) continue;
      const normalized = normalizeAnnotation(value);
      if (normalized) annotations[path] = normalized;
    }
    return {
      version: TRV_META_VERSION,
      ...(Object.keys(annotations).length ? { annotations } : {}),
    };
  } catch {
    return { version: TRV_META_VERSION };
  }
};

export const serializeTrvMeta = (meta: TrvMeta): string => {
  const annotations: EntryAnnotations = {};
  for (const [path, value] of Object.entries(meta.annotations ?? {})) {
    if (isHiddenMetaPath(path)) continue;
    const normalized = normalizeAnnotation(value);
    if (normalized) annotations[path] = normalized;
  }
  const payload: TrvMeta = {
    version: TRV_META_VERSION,
    ...(Object.keys(annotations).length ? { annotations } : {}),
  };
  return `${JSON.stringify(payload, null, 2)}\n`;
};

export const annotationsFromFiles = (
  files: Array<Pick<TrvMetaFile, 'path' | 'content'>>,
): EntryAnnotations | undefined => {
  const metaFile = files.find((file) => file.path === TRV_META_PATH);
  if (!metaFile?.content) return undefined;
  return parseTrvMeta(metaFile.content).annotations;
};

export const trvFile = (
  annotations: EntryAnnotations | undefined,
  modifiedAt = new Date().toISOString(),
): TrvMetaFile | null => {
  if (!annotations || !Object.keys(annotations).length) return null;
  const content = serializeTrvMeta({ version: TRV_META_VERSION, annotations });
  return {
    path: TRV_META_PATH,
    content,
    mimeType: TRV_MIME_TYPE,
    size: encoder.encode(content).length,
    modifiedAt,
  };
};

/** Upsert or remove the root `.trv` blob to match the given annotations map. */
export const withAnnotationsFile = <T extends TrvMetaFile>(
  files: T[],
  annotations: EntryAnnotations | undefined,
  modifiedAt = new Date().toISOString(),
): T[] => {
  const without = files.filter((file) => file.path !== TRV_META_PATH);
  const next = trvFile(annotations, modifiedAt);
  return next ? [...without, next as T] : without;
};

export type LegacyAnnotatedSnapshot = {
  files: TrvMetaFile[];
  /** @deprecated Prefer root `.trv`; migrated on load. */
  annotations?: EntryAnnotations;
};

/**
 * Lift snapshot-level `annotations` into a tracked `.trv` file (one source of truth).
 * File contents win on key conflicts. Strips the legacy field from the returned object.
 */
export const migrateLegacyAnnotations = <T extends LegacyAnnotatedSnapshot>(
  snapshot: T,
): Omit<T, 'annotations'> => {
  if (!('annotations' in snapshot)) return snapshot as Omit<T, 'annotations'>;
  const fromFile = annotationsFromFiles(snapshot.files);
  const legacy = snapshot.annotations;
  const { annotations: _legacy, ...rest } = snapshot;
  if (!legacy || !Object.keys(legacy).length) {
    return rest;
  }
  const merged: EntryAnnotations = { ...legacy, ...(fromFile ?? {}) };
  return {
    ...rest,
    files: withAnnotationsFile(rest.files, merged),
  };
};
